import {
    calculateNutritionFacts,
    generateEnglishTranslation,
} from './geminiService';
import type {
    Ingredient,
    InstructionStep,
    Language,
    NutritionFactsPer100g,
    Recipe,
} from '../types';

export interface FinalizePendingRecipeInput {
    readonly recipeId: string;
    readonly name: string;
    readonly ingredients: Ingredient[];
    readonly instructions: InstructionStep[];
    readonly servings: number;
    readonly originLanguage: Language;
    readonly needsNutrition: boolean;
    readonly needsTranslation: boolean;
    readonly existingEnglishTranslation?: Recipe['englishTranslation'];
    readonly existingNutrition?: NutritionFactsPer100g;
    readonly encryptedGeminiKey?: string;
}

export interface FinalizePendingRecipeResult {
    readonly nutritionFactsPer100g?: NutritionFactsPer100g;
    readonly englishTranslation?: Recipe['englishTranslation'];
    readonly saveStatus: 'ready';
    readonly updatedAt: string;
}

/**
 * Completes AI enrichment for a recipe that was already saved as `saveStatus: 'pending'`.
 */
export async function finalizePendingRecipe(
    input: FinalizePendingRecipeInput,
): Promise<FinalizePendingRecipeResult> {
    const now = new Date().toISOString();
    if (!input.needsNutrition && !input.needsTranslation) {
        return {
            ...(input.existingNutrition ? { nutritionFactsPer100g: input.existingNutrition } : {}),
            ...(input.existingEnglishTranslation ? { englishTranslation: input.existingEnglishTranslation } : {}),
            saveStatus: 'ready',
            updatedAt: now,
        };
    }

    let nutritionFactsPer100g = input.existingNutrition;
    let englishTranslation = input.existingEnglishTranslation;

    if (input.needsNutrition) {
        const facts = await calculateNutritionFacts(
            input.ingredients,
            input.servings,
            input.originLanguage,
            input.encryptedGeminiKey,
        );
        if (facts) {
            nutritionFactsPer100g = facts;
            if (englishTranslation && input.originLanguage !== 'en') {
                englishTranslation = { ...englishTranslation, nutritionFactsPer100g: facts };
            }
        }
    }

    if (input.needsTranslation) {
        const freshTrans = await generateEnglishTranslation(
            input.name,
            input.instructions,
            input.ingredients.map(({ name, unit, section }) => ({ name, unit, section })),
            input.encryptedGeminiKey,
        );
        if (freshTrans) {
            englishTranslation = nutritionFactsPer100g
                ? { ...freshTrans, nutritionFactsPer100g }
                : freshTrans;
        }
    }

    return {
        ...(nutritionFactsPer100g ? { nutritionFactsPer100g } : {}),
        ...(englishTranslation ? { englishTranslation } : {}),
        saveStatus: 'ready',
        updatedAt: now,
    };
}

export function buildFinalizeInputFromRecipe(
    recipe: Recipe,
    encryptedGeminiKey?: string,
): FinalizePendingRecipeInput {
    const originLanguage = (recipe.originLanguage || 'en') as Language;
    const instructions = Array.isArray(recipe.instructions)
        ? recipe.instructions
        : [{ id: 'legacy', type: 'step' as const, content: String(recipe.instructions || '') }];

    return {
        recipeId: recipe.id,
        name: recipe.name,
        ingredients: recipe.ingredients,
        instructions,
        servings: recipe.servings,
        originLanguage,
        needsNutrition: !recipe.nutritionFactsPer100g,
        needsTranslation: originLanguage !== 'en' && !recipe.englishTranslation,
        existingEnglishTranslation: recipe.englishTranslation,
        existingNutrition: recipe.nutritionFactsPer100g,
        encryptedGeminiKey,
    };
}
